import { ChevronDown } from "lucide-react";
import type { JSX } from "react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  V3_MENU_CONTENT,
  V3_MENU_ITEM,
  V3_MENU_LABEL,
  V3_MENU_SEPARATOR,
} from "@/components/ui/v3/menu";
import {
  type DictationMicrophone,
  getDictationMicrophone,
  listMicrophones,
  type MicrophoneInput,
  setDictationMicrophone,
} from "@/lib/dictationMicrophone";
import { cn } from "@/lib/utils";

const SAME_AS_RECORDING = "__same-as-recording__";

/**
 * The small chevron next to a dictation mic: switches the dictation
 * microphone without going to Settings. Same choice as Settings > Dictation
 * microphone (this browser); it applies from the next dictation, so the
 * chevron is disabled while one runs. The list is read when it opens.
 */
export const DictationMicMenu = ({
  disabled,
  variant = "default",
  className,
  list = listMicrophones,
}: {
  disabled?: boolean;
  // "v3": the v3.2 Ask bar.
  variant?: "default" | "v3";
  className?: string;
  // For tests and stories.
  list?: () => Promise<MicrophoneInput[]>;
}): JSX.Element => {
  const { t } = useTranslation();
  const v3 = variant === "v3";
  const [inputs, setInputs] = useState<MicrophoneInput[]>([]);
  const [chosen, setChosen] = useState<DictationMicrophone | null>(null);

  const refresh = useCallback(async () => {
    setChosen(getDictationMicrophone());
    try {
      setInputs(await list());
    } catch {
      setInputs([]);
    }
  }, [list]);

  const nameOf = (input: MicrophoneInput, index: number): string =>
    input.label ||
    t("settings.transcription.dictationMicrophone.unnamed", { n: index + 1 });
  const unnamed = inputs.length > 0 && inputs.every((input) => !input.label);
  const missing =
    chosen && !inputs.some((input) => input.deviceId === chosen.deviceId)
      ? chosen
      : null;

  // Names need microphone access once; the stream is closed right away.
  const allowNames = async (): Promise<void> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      stream.getTracks().forEach((track) => {
        track.stop();
      });
    } catch {
      // Blocked: the list keeps generic names.
    }
    await refresh();
  };

  const choose = (value: string): void => {
    if (value === SAME_AS_RECORDING) {
      setDictationMicrophone(null);
      setChosen(null);
      return;
    }
    const index = inputs.findIndex((input) => input.deviceId === value);
    const input = inputs[index];
    if (!input) return;
    const next = { deviceId: input.deviceId, label: nameOf(input, index) };
    setDictationMicrophone(next);
    setChosen(next);
  };

  const label = t("dictation.micMenu.choose");
  const item = cn("min-h-11 lg:min-h-9", v3 && V3_MENU_ITEM);
  // Radio items keep room for the dot on the left.
  const radio = cn(item, "pl-8");

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (open) void refresh();
      }}
    >
      <DropdownMenuTrigger asChild>
        <button
          aria-label={label}
          data-testid="dictation-mic-menu"
          disabled={disabled}
          title={label}
          type="button"
          className={cn(
            // Visibly 20px wide next to the mic; the tap area is 44px high.
            "relative inline-flex h-9 w-5 shrink-0 items-center justify-center rounded-full",
            "before:absolute before:-inset-x-1 before:-inset-y-1 before:content-['']",
            "focus-visible:outline-hidden focus-visible:ring-2 disabled:opacity-40",
            v3
              ? "text-v3-text-3 hover:text-v3-text focus-visible:ring-v3-brand-ring"
              : "text-muted-foreground hover:text-foreground focus-visible:ring-ring",
            "[&_svg]:size-4",
            className,
          )}
          onMouseDown={(event) => {
            // Keep the cursor in the field.
            event.preventDefault();
          }}
        >
          <ChevronDown aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className={cn("w-72 max-w-[90vw]", v3 && V3_MENU_CONTENT)}
        data-testid="dictation-mic-menu-content"
      >
        <DropdownMenuLabel className={cn(v3 && V3_MENU_LABEL)}>
          {t("settings.transcription.dictationMicrophone.label")}
        </DropdownMenuLabel>
        <p
          className={cn(
            "px-2 pb-2 text-xs text-muted-foreground",
            v3 && "px-3 text-v3-text-3",
          )}
        >
          {t("dictation.micMenu.hint")}
        </p>
        <DropdownMenuSeparator className={cn(v3 && V3_MENU_SEPARATOR)} />
        <DropdownMenuRadioGroup
          value={chosen?.deviceId ?? SAME_AS_RECORDING}
          onValueChange={choose}
        >
          <DropdownMenuRadioItem className={radio} value={SAME_AS_RECORDING}>
            {t("settings.transcription.dictationMicrophone.same")}
          </DropdownMenuRadioItem>
          {inputs.map((input, index) => (
            <DropdownMenuRadioItem
              key={input.deviceId}
              className={radio}
              value={input.deviceId}
            >
              <span className="min-w-0 truncate">{nameOf(input, index)}</span>
            </DropdownMenuRadioItem>
          ))}
          {missing && (
            <DropdownMenuRadioItem
              disabled
              className={radio}
              value={missing.deviceId}
            >
              <span className="min-w-0 truncate">
                {t("settings.transcription.dictationMicrophone.missingSaved", {
                  name: missing.label || missing.deviceId.slice(0, 8),
                })}
              </span>
            </DropdownMenuRadioItem>
          )}
        </DropdownMenuRadioGroup>
        {unnamed && (
          <>
            <DropdownMenuSeparator className={cn(v3 && V3_MENU_SEPARATOR)} />
            <DropdownMenuItem
              className={item}
              onSelect={(event) => {
                // Stay open to show the names.
                event.preventDefault();
                void allowNames();
              }}
            >
              {t("settings.transcription.dictationMicrophone.allow")}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
